// Featured work: the homepage shows one card per project, and /work/<id> is
// the project page. Every screenshot shows sample data -- the companies,
// people, orders and patients on screen are made up. The only real content is
// the welding footage in the StandardIQ operator screen.
//
// Wording here was fact-checked against each product's codebase; keep claims
// to what the software demonstrably does.

const BASE = '/media/work';

const work = [
    {
        id: 'standardiq',
        name: 'StandardIQ',
        title: 'StandardIQ: Shop-Floor Work Instructions & AI Vision | minneAI',
        eyebrow: 'Manufacturing · Shop floor',
        partners: [{ name: 'Lean Partners' }, { name: 'Grip-Tite' }],
        tagline: 'Work instructions, live tracking and AI-assisted checks at every workstation.',
        summary:
            'StandardIQ is a workstation application for the production floor. Operators ' +
            'follow step-by-step work instructions — video, photo, PDF or text — and each ' +
            'step advances the way the work calls for: a purpose-built button, a ' +
            'timer, the end of a video, or a camera that recognizes when the step is ' +
            'done. Supervisors see every station’s progress against standard in real ' +
            'time, and reporting identifies the specific steps where time is lost.',
        scope: [
            'Operator workstation app (desktop)',
            'Work instructions as video, photo, PDF or text',
            'Steps advance by button, timer, end of video or camera',
            'Advance button — our hardware and firmware',
            'On-device AI vision: step recognition and quality checks',
            'Live supervisor dashboard with help requests',
            'ERP integration for orders and employees; electronic sign-offs',
            'Reporting: throughput, labor hours, defects, step-level time loss',
        ],
        seo: 'StandardIQ: shop-floor workstation software with step-by-step work instructions, on-device AI vision checks, a live supervisor dashboard and step-level reporting.',
        screens: [
            {
                src: `${BASE}/standardiq/live-dashboard.webp`,
                w: 2000, h: 1125,
                alt: 'StandardIQ supervisor dashboard showing every station live: operator, order, ahead or behind standard, and who needs help',
                caption: 'Every station live: ahead or behind standard, and who needs help.',
                frame: 'desktop',
            },
            {
                src: `${BASE}/standardiq/operator-instructions.webp`,
                w: 2000, h: 1125,
                alt: 'StandardIQ operator station: a video work instruction of a weld with the step written underneath, plus order, quantity and timer',
                caption: 'Each step as a video at the station, with the written instruction underneath.',
                frame: 'desktop',
            },
            {
                src: `${BASE}/standardiq/reporting.webp`,
                w: 2000, h: 1172,
                alt: 'StandardIQ reporting summary: units, labor hours, time versus standard and defects, compared with the prior 30 days',
                caption: 'Units, labor hours and defects, compared with the prior 30 days.',
                frame: 'desktop',
            },
            {
                src: `${BASE}/standardiq/step-friction.webp`,
                w: 2000, h: 1563,
                alt: 'StandardIQ step friction report ranking which individual work steps lose the most time',
                caption: 'The steps that lose the most time, ranked.',
                frame: 'desktop',
            },
        ],
    },
    {
        id: 'scheduleiq',
        name: 'ScheduleIQ',
        title: 'ScheduleIQ: Production Scheduling with ERP Integration | minneAI',
        eyebrow: 'Manufacturing · Production scheduling',
        partners: [{ name: 'Lean Partners' }, { name: 'Grip-Tite' }],
        tagline: 'Make-or-buy planning that pulls from the ERP and shows every calculation.',
        summary:
            'ScheduleIQ is production scheduling software for a manufacturer. It ' +
            'synchronizes orders, work orders and inventory from the ERP, compares each ' +
            'part’s available supply against demand, and recommends what to make or ' +
            'buy, in what quantity, and by when — with every calculation visible on ' +
            'screen. Shop load, daily build plans, shipping readiness and inventory ' +
            'valuation are managed in the same tool.',
        scope: [
            'Live integration with the ERP for orders, work orders and inventory',
            'Make-or-buy recommendations with lot sizing and release dates',
            'Work-center capacity view with overloads flagged',
            'Shipping readiness, kit tracking and inventory valuation',
        ],
        seo: 'ScheduleIQ: production scheduling software with live ERP integration, make-or-buy planning and capacity views, built by minneAI for a Minnesota manufacturer.',
        screens: [
            {
                src: `${BASE}/scheduleiq/schedule.webp`,
                w: 2000, h: 1250,
                alt: 'ScheduleIQ schedule screen listing every part with on-hand, shortage, act-by date and team notes',
                caption: 'Every part, what’s short, and when to act, in one list.',
            },
            {
                src: `${BASE}/scheduleiq/drilldown.webp`,
                w: 2000, h: 1250,
                alt: 'ScheduleIQ part drill-down: the sum from on hand to shortage, and the customer orders behind it with the first unfillable order highlighted',
                caption: 'Every number adds up on screen, down to the first order that can’t be filled.',
            },
            {
                src: `${BASE}/scheduleiq/shop-load.webp`,
                w: 2000, h: 1250,
                alt: 'ScheduleIQ shop load view: utilization of each work center day by day with overloaded days flagged',
                caption: 'Load on each work center, day by day, with overloads flagged.',
            },
            {
                src: `${BASE}/scheduleiq/shipping.webp`,
                w: 2000, h: 1047,
                alt: 'ScheduleIQ shipping screen: every open order line, earliest first, marked ready, partial or waiting on a build or purchase',
                caption: 'Shipping sees what can go out today, line by line.',
            },
        ],
    },
    {
        id: 'walkport',
        // Off the homepage until the client clears the screenshots; the page
        // itself stays reachable at /work/walkport.
        listed: false,
        name: 'WalkPort',
        title: 'WalkPort: Software for an FDA-Registered Walk Station | minneAI',
        eyebrow: 'Medical device · Rehabilitation',
        partners: [{ name: 'Medthera' }],
        tagline: 'The complete software platform for an FDA-registered walk station.',
        summary:
            'WalkPort is an FDA-registered walk station that supports standing and ' +
            'walking therapy. minneAI delivered its software end to end: the firmware ' +
            'that reads the machine’s sensors, a tablet application mounted on the ' +
            'device that records every step in real time, a managed fleet of tablets ' +
            'that updates itself, the cloud backend, and a web portal where clinicians ' +
            'review each patient’s progress.',
        scope: [
            'Sensor firmware and hardware selection',
            'Tablet application, mounted on the device',
            'Managed, self-updating tablet fleet',
            'Cloud backend that keeps every session in sync',
            'Clinician web portal with session history and trends',
        ],
        film: true,
        seo: 'WalkPort: the software platform for an FDA-registered walk station — sensor firmware, a device-mounted tablet app, a managed tablet fleet and a clinician web portal, built by minneAI.',
        screens: [
            {
                src: `${BASE}/walkport/tablet-session.webp`,
                w: 1920, h: 1080,
                alt: 'WalkPort tablet, mounted on the machine, during a session: an animated walking figure with time, steps, pace and distance beside it',
                caption: 'Time, steps, pace and distance, live. The avatar walks when the patient walks.',
                frame: 'tablet',
            },
            {
                src: `${BASE}/walkport/tablet-set-goal.webp`,
                w: 1920, h: 1080,
                alt: 'WalkPort tablet screen for choosing a session goal by time, steps or distance',
                caption: 'Set a goal by time, steps or distance.',
                frame: 'tablet',
            },
            {
                src: `${BASE}/walkport/clinic-sessions.webp`,
                w: 2000, h: 1250,
                alt: 'WalkPort clinician portal showing one patient’s session history with totals and trend lines',
                caption: 'What clinicians see: every session, with totals and trends.',
                frame: 'browser',
            },
        ],
    },
    {
        id: 'moneybuddy',
        name: 'My Little Money Buddy',
        title: 'My Little Money Buddy: Free AI Financial Check-Up Tool | minneAI',
        eyebrow: 'Consumer · Personal finance',
        tagline: 'A free AI financial check-up: a short questionnaire becomes a personal report.',
        summary:
            'My Little Money Buddy is a free web tool built around the principles of ' +
            'the book My Little Book of Big Money. A guided questionnaire walks ' +
            'someone through their income, expenses, debts, savings and goals; an AI ' +
            'model then writes a one-page, plain-language report with specific next ' +
            'steps, which they can print or save as a PDF. Nothing they enter is stored.',
        scope: [
            'Guided multi-step questionnaire with inline help',
            'AI-generated personal finance report in plain language',
            'Report writing grounded in the book’s principles',
            'Print and save-as-PDF output',
            'Privacy by design: submissions are not retained',
            'Animated character and intro sequence',
        ],
        seo: 'My Little Money Buddy: a free AI-powered personal finance tool that turns a short questionnaire into a one-page report, built by minneAI.',
        screens: [
            {
                src: `${BASE}/moneybuddy/welcome.webp`,
                w: 1160, h: 740,
                alt: 'My Little Money Buddy welcome screen explaining the tool, with a Get Started button',
                caption: 'What the tool is, what it isn’t, and one button to begin.',
                frame: 'browser',
            },
            {
                src: `${BASE}/moneybuddy/expenses.webp`,
                w: 1440, h: 740,
                alt: 'My Little Money Buddy monthly expenses step with fields for rent, utilities, loans and spending',
                caption: 'Monthly expenses, one step of the guided questionnaire.',
                frame: 'browser',
            },
            {
                src: `${BASE}/moneybuddy/review.webp`,
                w: 1440, h: 740,
                alt: 'My Little Money Buddy review screen summarizing the entered income and expenses before submission',
                caption: 'Everything entered, reviewed in one place before the report is written.',
                frame: 'browser',
            },
        ],
    },
];

export const listedWork = work.filter((p) => p.listed !== false);
export const findProject = (id) => work.find((p) => p.id === id);
export const nextProject = (id) => {
    const pool = listedWork.some((p) => p.id === id) ? listedWork : work;
    return pool[(pool.findIndex((p) => p.id === id) + 1) % pool.length];
};

export default work;
